import React, { useState, useEffect, useRef } from "react";
import SpreetailHeader from "../layout/SpreetailHeader";
import { RCAPlan, PlanStep } from "../../../shared/rcaApiService";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ProgressSheet from "../common/ProgressSheet";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface RCAPlanViewProps {
    plan: RCAPlan;
    userQuestion: string;
    tableData: { headers: string[]; rows: any[][] };
    onExecute: (skipValidation: boolean) => void;  // v2.4.0: Updated to pass skip_validation flag
    onRegenerate: () => void;
    onPlanReorder: (reorderedSteps: PlanStep[]) => void;
    isExecuting: boolean;
    progress?: number;
    progressMessage?: string;
    onPlanChange?: (updatedPlan: RCAPlan) => void;
    validationError?: string;  // v2.3.0: Error from backend (validation or other)
}

interface SortableStepCardProps {
    step: PlanStep;
    index: number;
    disabled: boolean;
    isEditing: boolean;
    isDeleting: boolean;
    isLocked?: boolean;  // True for rca_analysis step - cannot be reordered
    editableRef: (el: HTMLDivElement | null) => void;
    onStartEdit: () => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
    onDelete: () => void;
    onConfirmDelete: () => void;
    onCancelDelete: () => void;
}

const SortableStepCard: React.FC<SortableStepCardProps> = ({
    step,
    index,
    disabled,
    isEditing,
    isDeleting,
    isLocked = false,
    editableRef,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete,
    onConfirmDelete,
    onCancelDelete,
}) => {
    // Disable drag when editing or when step is locked (rca_analysis)
    const isDragDisabled = disabled || isEditing || isLocked;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: step.step_id, disabled: isDragDisabled });

    // Build transform with scale effect when dragging
    const transformString = CSS.Transform.toString(transform);
    const finalTransform = isDragging && transformString
        ? `${transformString} scale(1.02)`
        : transformString;

    const style: React.CSSProperties = {
        transform: finalTransform,
        transition: isDragging
            ? "box-shadow 0.2s cubic-bezier(0.25, 1, 0.5, 1), background 0.2s ease, border-color 0.2s ease"
            : "transform 0.25s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.25s ease, background 0.2s ease, border-color 0.2s ease",
        zIndex: isDragging ? 1000 : 1,
        position: "relative" as const,
    };

    // Only apply drag listeners to the handle when not editing
    const dragListeners = isEditing ? {} : listeners;

    return (
        <div className="step-row">
            {/* Step Number Badge - Outside the card on the left */}
            <div className="step-number">{index + 1}</div>
            
            <div
                ref={setNodeRef}
                style={style}
                className={`plan-step ${isDragging ? "dragging" : ""} ${isEditing ? "editing" : ""}`}
                {...attributes}
            >
                {/* Drag Handle - 6-dot vertical grip */}
                <div
                    className={`drag-handle ${isDragDisabled ? "disabled" : ""} ${isLocked ? "locked" : ""}`}
                    {...dragListeners}
                    title={
                        isLocked ? "Analysis step must remain last" :
                        isEditing ? "" :
                        disabled ? "Cannot reorder while executing" :
                        "Drag to reorder"
                    }
                >
                    <DragIndicatorIcon sx={{ fontSize: 20, color: isDragDisabled ? "#D1D5DB" : "#9CA3AF" }} />
                </div>

                <div className="step-content">
                    <div className="step-header">
                        <div className="step-header-content">
                            <h3 className="step-title">{getStepTitle(step.step_id)}</h3>
                        </div>
                    {!isEditing && (
                        <div className="step-action-buttons">
                            <button
                                className="step-reason-edit-btn"
                                onClick={onStartEdit}
                                disabled={disabled}
                                title="Edit reason"
                            >
                                {/* Pencil icon matching Figma - teal color, diagonal pencil with line */}
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 20H8L18.5 9.5C19.0304 8.96957 19.3284 8.25014 19.3284 7.5C19.3284 6.74986 19.0304 6.03043 18.5 5.5C17.9696 4.96957 17.2501 4.67157 16.5 4.67157C15.7499 4.67157 15.0304 4.96957 14.5 5.5L4 16V20Z" stroke="#068E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M13.5 6.5L17.5 10.5" stroke="#068E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M4 24H20" stroke="#068E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <button
                                className="step-delete-btn"
                                onClick={onDelete}
                                disabled={disabled}
                                title="Delete step"
                            >
                                {/* Trash icon matching Figma - red/pink color with horizontal lines */}
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 7H20" stroke="#E11D48" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 4H14" stroke="#E11D48" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M6 7V19C6 20.1046 6.89543 21 8 21H16C17.1046 21 18 20.1046 18 19V7" stroke="#E11D48" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M9 11H15" stroke="#E11D48" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M9 15H15" stroke="#E11D48" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            {isEditing ? (
                <>
                    <div
                        ref={editableRef}
                        className="step-reason-editable"
                        contentEditable
                        suppressContentEditableWarning
                        data-placeholder="Enter reason for this step..."
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {step.reason}
                    </div>
                    <div className="edit-actions">
                        <button
                            className="edit-action-btn save"
                            onClick={onSaveEdit}
                        >
                            Save
                        </button>
                        <button
                            className="edit-action-btn cancel"
                            onClick={onCancelEdit}
                        >
                            Cancel
                        </button>
                    </div>
                </>
            ) : (
                <p className="step-reason">{step.reason}</p>
            )}
                {isDeleting && (
                    <div className="delete-confirmation">
                        <p className="delete-confirmation-text">Delete this step?</p>
                        <div className="delete-confirmation-actions">
                            <button
                                className="delete-confirm-btn yes"
                                onClick={onConfirmDelete}
                            >
                                Yes
                            </button>
                            <button
                                className="delete-confirm-btn no"
                                onClick={onCancelDelete}
                            >
                                No
                            </button>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

// Extract base step type from step_id (e.g., "smart_column_2" -> "smart_column")
const getBaseStepType = (stepId: string): string => {
    return stepId.replace(/_\d+$/, '');
};

// Generate unique step ID for adding duplicate step types
const generateUniqueStepId = (baseType: string, existingSteps: PlanStep[]): string => {
    const existingIds = existingSteps.map(s => s.step_id);

    // If base type doesn't exist, return it as-is
    if (!existingIds.includes(baseType)) {
        return baseType;
    }

    // Find highest counter
    let maxCounter = 1;
    const regex = new RegExp(`^${baseType}_(\\d+)$`);
    existingIds.forEach(id => {
        const match = id.match(regex);
        if (match) {
            maxCounter = Math.max(maxCounter, parseInt(match[1], 10));
        }
    });

    return `${baseType}_${maxCounter + 1}`;
};

// Get default reason for step type when adding new steps
const getDefaultReason = (stepType: string): string => {
    switch (stepType) {
        case "smart_column":
            return "Add calculated columns to enhance data analysis";
        case "pivot_table":
            return "Create a pivot table for data aggregation";
        default:
            return "Perform additional analysis step";
    }
};

const getStepTitle = (stepId: string): string => {
    const baseType = getBaseStepType(stepId);
    // Extract counter if present for display
    const match = stepId.match(/_(\d+)$/);
    const suffix = match ? ` (${match[1]})` : '';

    switch (baseType) {
        case "smart_column":
            return `Generate Smart Columns${suffix}`;
        case "pivot_table":
            return `Create Pivot Table${suffix}`;
        case "rca_analysis":
            return "Analysis";
        default:
            return stepId;
    }
};

const RCAPlanView: React.FC<RCAPlanViewProps> = ({
    plan,
    userQuestion: _userQuestion,  // Kept for interface compatibility, not displayed in this view
    onExecute,
    onRegenerate,
    onPlanReorder,
    isExecuting,
    progress = 0,
    progressMessage = "",
    onPlanChange,
    validationError
}) => {
    // Suppress unused variable warning
    void _userQuestion;
    
    // Local state for editable plan
    const [editablePlan, setEditablePlan] = useState<RCAPlan>(plan);
    const [editingStepId, setEditingStepId] = useState<string | null>(null);
    const [originalReason, setOriginalReason] = useState<string>("");
    const [deletingStepId, setDeletingStepId] = useState<string | null>(null);
    const editableRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const validationErrorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (validationError && validationErrorRef.current) {
            validationErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [validationError]);

    // v2.4.0: Track if user can skip validation (after seeing validation error)
    const [canSkipValidation, setCanSkipValidation] = useState<boolean>(false);

    // v2.5.0: Add step UI state
    const [isAddingStep, setIsAddingStep] = useState<boolean>(false);
    const [addStepType, setAddStepType] = useState<"smart_column" | "pivot_table" | null>(null);
    const [newStepReason, setNewStepReason] = useState<string>("");

    // Sync local plan with prop when it changes
    useEffect(() => {
        setEditablePlan(plan);
    }, [plan]);

    // v2.4.0: Update canSkipValidation when validation error appears/disappears
    useEffect(() => {
        if (validationError) {
            // Validation failed - allow user to skip on next attempt
            setCanSkipValidation(true);
        } else {
            // No error (plan modified or initial state) - reset skip option
            setCanSkipValidation(false);
        }
    }, [validationError]);

    // Filter to only show enabled steps
    const enabledSteps = editablePlan.steps.filter(step => step.enabled);

    const handleStartEdit = (stepId: string) => {
        if (isExecuting) return;

        // Store original reason for cancel functionality
        const step = editablePlan.steps.find(s => s.step_id === stepId);
        if (step) {
            setOriginalReason(step.reason);
        }

        setEditingStepId(stepId);
        // Focus the editable element after state update
        setTimeout(() => {
            const editableEl = editableRefs.current[stepId];
            if (editableEl) {
                editableEl.focus();
                // Move cursor to end
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(editableEl);
                range.collapse(false);
                sel?.removeAllRanges();
                sel?.addRange(range);
            }
        }, 0);
    };

    const handleSaveEdit = (stepId: string) => {
        const editableEl = editableRefs.current[stepId];
        if (!editableEl) return;

        const newReason = editableEl.innerText.trim();
        const originalStep = editablePlan.steps.find(s => s.step_id === stepId);

        if (!originalStep) return;

        // Check if reason actually changed
        if (newReason === originalReason) {
            setEditingStepId(null);
            setOriginalReason("");
            return;
        }

        // Create modified step and update plan locally
        const modifiedStep: PlanStep = {
            ...originalStep,
            reason: newReason
        };

        const updatedSteps = editablePlan.steps.map(step =>
            step.step_id === stepId ? modifiedStep : step
        );
        const updatedPlan = { ...editablePlan, steps: updatedSteps };
        setEditablePlan(updatedPlan);

        // Notify parent of the change
        if (onPlanChange) {
            onPlanChange(updatedPlan);
        }

        setEditingStepId(null);
        setOriginalReason("");
    };

    const handleCancelEdit = (stepId: string) => {
        // Restore original reason
        const editableEl = editableRefs.current[stepId];
        if (editableEl) {
            editableEl.innerText = originalReason;
        }
        setEditingStepId(null);
        setOriginalReason("");
    };

    const handleDeleteStep = (stepId: string) => {
        if (isExecuting) return;
        // Show inline confirmation
        setDeletingStepId(stepId);
    };

    const handleConfirmDelete = (stepId: string) => {
        // Set enabled to false to "delete" the step
        const updatedSteps = editablePlan.steps.map(s =>
            s.step_id === stepId ? { ...s, enabled: false } : s
        );
        const updatedPlan = { ...editablePlan, steps: updatedSteps };
        setEditablePlan(updatedPlan);

        // Notify parent of the change
        if (onPlanChange) {
            onPlanChange(updatedPlan);
        }
        setDeletingStepId(null);
    };

    const handleCancelDelete = () => {
        setDeletingStepId(null);
    };

    // v2.5.0: Handle adding a new step to the plan
    const handleAddStep = () => {
        if (!addStepType || !newStepReason.trim()) return;

        const newStepId = generateUniqueStepId(addStepType, editablePlan.steps);

        const newStep: PlanStep = {
            step_id: newStepId,
            enabled: true,
            reason: newStepReason.trim()
        };

        // Find rca_analysis index - new steps must be inserted before it
        const rcaIndex = editablePlan.steps.findIndex(s => getBaseStepType(s.step_id) === "rca_analysis");

        // Insert before rca_analysis or at end if not found
        const insertIndex = rcaIndex >= 0 ? rcaIndex : editablePlan.steps.length;

        const newSteps = [
            ...editablePlan.steps.slice(0, insertIndex),
            newStep,
            ...editablePlan.steps.slice(insertIndex)
        ];

        const updatedPlan = { ...editablePlan, steps: newSteps };
        setEditablePlan(updatedPlan);

        // Notify parent of the change (triggers validation reset)
        if (onPlanChange) {
            onPlanChange(updatedPlan);
        }

        // Reset add step state
        setIsAddingStep(false);
        setAddStepType(null);
        setNewStepReason("");
    };

    // Setup sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // 5px movement required before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end - reorder steps
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = enabledSteps.findIndex((step) => step.step_id === active.id);
            const newIndex = enabledSteps.findIndex((step) => step.step_id === over.id);

            // Simulate the reorder first
            const reorderedEnabledSteps = arrayMove(enabledSteps, oldIndex, newIndex);

            // Ensure rca_analysis always remains the last step
            const lastStep = reorderedEnabledSteps[reorderedEnabledSteps.length - 1];
            if (lastStep && getBaseStepType(lastStep.step_id) !== "rca_analysis") {
                // The move would displace analysis from the last position - block it
                return;
            }

            // Rebuild full steps array: disabled steps stay in place, enabled steps are reordered
            const disabledSteps = plan.steps.filter(step => !step.enabled);
            const allSteps = [...reorderedEnabledSteps, ...disabledSteps];

            onPlanReorder(allSteps);
        }
    };

    return (
        <>
            <style>
                {`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .bg-gradient-custom {
                    background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
                }
                .hide-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .hide-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                
                /* Parent Transparent Card for Execution Steps Section */
                .execution-steps-container {
                    background: rgba(255, 255, 255, 0.5);
                    border-radius: 16px;
                    padding: 16px;
                    margin-bottom: 16px;
                }
                
                /* Execution Steps Section Header */
                .execution-steps-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    margin-bottom: 16px;
                }
                .execution-steps-icon {
                    width: 28px;
                    height: 28px;
                    flex-shrink: 0;
                }
                .execution-steps-title-container {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .execution-steps-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1F2937;
                    margin: 0;
                }
                .execution-steps-subtitle {
                    font-size: 11px;
                    color: #9CA3AF;
                    margin: 0;
                    font-weight: 400;
                }
                
                /* Step Row - Contains number badge and card */
                .step-row {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 10px;
                }
                .step-row:last-child {
                    margin-bottom: 0;
                }
                
                /* Step Number Badge - Teal circle outside the card, vertically centered */
                .step-number {
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    background: #068E75;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 600;
                    flex-shrink: 0;
                }
                
                /* Plan Step Card - Figma transparent card design */
                .plan-step {
                    display: flex;
                    align-items: flex-start;
                    flex: 1;
                    background: rgba(255, 255, 255, 0.85);
                    border: 1.5px solid transparent;
                    border-radius: 12px;
                    padding: 12px 14px;
                    transition: all 0.2s ease;
                    position: relative;
                }
                .plan-step:hover:not(.dragging):not(.editing) {
                    background: rgba(255, 255, 255, 0.95);
                }
                .plan-step.editing,
                .plan-step.dragging {
                    border-color: #068E75;
                    background: rgba(255, 255, 255, 1);
                    box-shadow: 0 4px 12px rgba(6, 142, 117, 0.12);
                }
                
                /* Drag Handle - 6-dot vertical grip indicator */
                .drag-handle {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 4px 2px;
                    margin-right: 8px;
                    cursor: grab;
                    border-radius: 4px;
                    transition: all 0.15s ease;
                    flex-shrink: 0;
                    touch-action: none;
                    margin-top: 2px;
                }
                .drag-handle:hover:not(.disabled):not(.locked) {
                    background: rgba(107, 114, 128, 0.1);
                }
                .drag-handle:active:not(.disabled):not(.locked) {
                    cursor: grabbing;
                }
                .drag-handle.disabled,
                .drag-handle.locked {
                    cursor: not-allowed;
                    opacity: 0.4;
                }
                
                .step-content {
                    flex: 1;
                    min-width: 0;
                }
                .step-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    margin-bottom: 6px;
                }
                .step-header-content {
                    display: flex;
                    align-items: center;
                    gap: 0;
                    flex: 1;
                }
                
                .step-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1F2937;
                    margin: 0;
                }
                .step-reason {
                    font-size: 12px;
                    color: #6B7280;
                    line-height: 1.6;
                    margin: 0;
                }
                .step-reason-editable {
                    font-size: 12px;
                    color: #6B7280;
                    line-height: 1.6;
                    margin: 0;
                    padding: 8px 10px;
                    border: 1.5px solid #068E75;
                    border-radius: 8px;
                    background: white;
                    min-height: 20px;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 0 0 3px rgba(6, 142, 117, 0.1);
                    white-space: pre-wrap;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    cursor: text;
                }
                .step-reason-editable:focus {
                    border-color: #068E75;
                    box-shadow: 0 0 0 3px rgba(6, 142, 117, 0.15);
                    color: #374151;
                }
                .step-reason-editable:empty:before {
                    content: attr(data-placeholder);
                    color: #9CA3AF;
                    font-style: italic;
                }
                
                /* Action Buttons Container */
                .step-action-buttons {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-shrink: 0;
                }
                
                /* Edit Button - Pencil icon matching Figma (teal color, no background) */
                .step-reason-edit-btn {
                    background: none !important;
                    background-color: transparent !important;
                    border: none;
                    padding: 2px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: opacity 0.2s ease;
                    opacity: 0.9;
                    flex-shrink: 0;
                    outline: none;
                    box-shadow: none;
                }
                .step-reason-edit-btn:hover {
                    opacity: 1;
                    background: transparent !important;
                }
                .step-reason-edit-btn:disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                }
                
                /* Delete Button - Trash icon matching Figma (red/pink color, no background) */
                .step-delete-btn {
                    background: none !important;
                    background-color: transparent !important;
                    border: none;
                    padding: 2px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: opacity 0.2s ease;
                    opacity: 0.9;
                    flex-shrink: 0;
                    outline: none;
                    box-shadow: none;
                }
                .step-delete-btn:hover {
                    opacity: 1;
                    background: transparent !important;
                }
                .step-delete-btn:disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                }
                .step-reason-edit-btn.editing {
                    opacity: 1;
                }
                .edit-actions {
                    display: flex;
                    gap: 8px;
                    margin-top: 10px;
                    justify-content: flex-end;
                }
                .edit-action-btn {
                    padding: 6px 14px;
                    font-size: 12px;
                    font-weight: 500;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                .edit-action-btn.save {
                    background: linear-gradient(135deg, #068E75 0%, #057A63 100%);
                    color: white;
                    border: none;
                    box-shadow: 0 1px 3px rgba(6, 142, 117, 0.3);
                }
                .edit-action-btn.save:hover {
                    background: linear-gradient(135deg, #057A63 0%, #046B56 100%);
                    transform: translateY(-1px);
                    box-shadow: 0 2px 6px rgba(6, 142, 117, 0.4);
                }
                .edit-action-btn.cancel {
                    background: white;
                    color: #64748B;
                    border: 1px solid #E5E7EB;
                }
                .edit-action-btn.cancel:hover {
                    background: #F9FAFB;
                    border-color: #D1D5DB;
                    color: #374151;
                }
                .delete-confirmation {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background: rgba(220, 38, 38, 0.08);
                    border: 1px solid rgba(220, 38, 38, 0.2);
                    border-radius: 8px;
                    padding: 10px 12px;
                    margin-top: 10px;
                }
                .delete-confirmation-text {
                    font-size: 12px;
                    color: #991B1B;
                    font-weight: 500;
                    margin: 0;
                }
                .delete-confirmation-actions {
                    display: flex;
                    gap: 8px;
                }
                .delete-confirm-btn {
                    padding: 5px 12px;
                    font-size: 11px;
                    font-weight: 500;
                    border-radius: 5px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .delete-confirm-btn.yes {
                    background: #DC2626;
                    color: white;
                    border: none;
                }
                .delete-confirm-btn.yes:hover {
                    background: #B91C1C;
                }
                .delete-confirm-btn.no {
                    background: white;
                    color: #64748B;
                    border: 1px solid #E5E7EB;
                }
                .delete-confirm-btn.no:hover {
                    background: #F9FAFB;
                    border-color: #D1D5DB;
                }
                /* Validation Feedback Styles */
                .validation-feedback {
                    margin-top: 10px;
                    margin-left: 40px;
                    padding: 10px 12px;
                    border-radius: 8px;
                    font-size: 12px;
                    animation: slideIn 0.2s ease-out;
                }
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-8px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .validation-feedback.valid {
                    background: rgba(5, 150, 105, 0.08);
                    border: 1px solid rgba(5, 150, 105, 0.2);
                }
                .validation-feedback.invalid {
                    background: rgba(220, 38, 38, 0.08);
                    border: 1px solid rgba(220, 38, 38, 0.2);
                }
                .validation-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                }
                .validation-message {
                    flex: 1;
                    line-height: 1.4;
                    color: #374151;
                }
                .validation-feedback.valid .validation-message {
                    color: #065F46;
                }
                .validation-feedback.invalid .validation-message {
                    color: #991B1B;
                }
                .validation-dismiss {
                    background: transparent;
                    border: none;
                    font-size: 18px;
                    line-height: 1;
                    cursor: pointer;
                    color: #9CA3AF;
                    padding: 0;
                    width: 20px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: all 0.15s ease;
                }
                .validation-dismiss:hover {
                    background: rgba(0, 0, 0, 0.05);
                    color: #374151;
                }
                .validation-issues {
                    margin: 8px 0 0 24px;
                    padding: 0;
                    list-style: disc;
                    color: #991B1B;
                }
                .validation-issues li {
                    margin-bottom: 4px;
                }
                .validation-suggestions {
                    margin-top: 8px;
                    padding-top: 8px;
                    border-top: 1px solid rgba(0, 0, 0, 0.06);
                }
                .suggestions-label {
                    font-weight: 500;
                    color: #4B5563;
                    display: block;
                    margin-bottom: 4px;
                }
                .validation-suggestions ul {
                    margin: 0 0 0 24px;
                    padding: 0;
                    list-style: disc;
                    color: #4B5563;
                }
                .validation-suggestions li {
                    margin-bottom: 4px;
                }
                .edit-action-btn.save:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                /* Progress Section - Inline below plan steps */
                .progress-section {
                    background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.75));
                    border: 1px solid rgba(6, 142, 117, 0.2);
                    border-radius: 10px;
                    padding: 16px;
                    margin-bottom: 16px;
                }
                .progress-message {
                    font-size: 12px;
                    color: #065F46;
                    margin: 10px 0 0 0;
                    text-align: center;
                    font-weight: 500;
                }
                .validation-error-banner {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    background: rgba(220, 38, 38, 0.08);
                    border: 1px solid rgba(220, 38, 38, 0.2);
                    border-radius: 10px;
                    padding: 12px 14px;
                    margin-bottom: 16px;
                    font-size: 13px;
                    color: #991B1B;
                    line-height: 1.5;
                }
                /* Add Step Section Styles */
                .add-step-section {
                    margin-top: 12px;
                    margin-left: 38px;
                }
                .add-step-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    width: 100%;
                    padding: 10px 16px;
                    background: rgba(255, 255, 255, 0.7);
                    border: 1.5px dashed rgba(6, 142, 117, 0.4);
                    border-radius: 10px;
                    color: #068E75;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .add-step-btn:hover {
                    background: rgba(6, 142, 117, 0.08);
                    border-color: #068E75;
                }
                .add-step-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .add-step-menu {
                    background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.75));
                    border: 1px solid rgba(6, 142, 117, 0.2);
                    border-radius: 10px;
                    padding: 12px;
                }
                .add-step-menu-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #64748B;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin: 0 0 10px 0;
                }
                .add-step-option {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    width: 100%;
                    padding: 10px 12px;
                    background: white;
                    border: 1px solid #E5E7EB;
                    border-radius: 8px;
                    font-size: 13px;
                    color: #0F172A;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    margin-bottom: 8px;
                }
                .add-step-option:hover {
                    background: rgba(6, 142, 117, 0.05);
                    border-color: rgba(6, 142, 117, 0.3);
                }
                .add-step-cancel {
                    width: 100%;
                    padding: 8px;
                    background: transparent;
                    border: none;
                    font-size: 12px;
                    color: #64748B;
                    cursor: pointer;
                }
                .add-step-cancel:hover {
                    color: #0F172A;
                }
                .add-step-form {
                    background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.75));
                    border: 1px solid rgba(6, 142, 117, 0.2);
                    border-radius: 10px;
                    padding: 12px;
                }
                .add-step-form-label {
                    font-size: 12px;
                    font-weight: 500;
                    color: #374151;
                    margin: 0 0 8px 0;
                }
                .add-step-reason-input {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #D1D5DB;
                    border-radius: 8px;
                    font-size: 12px;
                    color: #374151;
                    resize: none;
                    outline: none;
                    box-sizing: border-box;
                    font-family: inherit;
                }
                .add-step-reason-input:focus {
                    border-color: #068E75;
                    box-shadow: 0 0 0 2px rgba(6, 142, 117, 0.1);
                }
                .add-step-form-actions {
                    display: flex;
                    gap: 8px;
                    margin-top: 10px;
                    justify-content: flex-end;
                }
                .add-step-confirm {
                    padding: 6px 16px;
                    background: linear-gradient(135deg, #068E75 0%, #057A63 100%);
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .add-step-confirm:hover {
                    background: linear-gradient(135deg, #057A63 0%, #046B56 100%);
                }
                .add-step-confirm:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                `}
            </style>
            <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
                <div className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
                    style={{ background: 'radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)' }}></div>
                <div className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
                    style={{ background: 'radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)' }}></div>

                <SpreetailHeader />

                <div className="flex-1 overflow-y-auto px-5 pb-5 min-h-0 hide-scrollbar">
                    <div className="max-w-md mx-auto pt-3">
                        {/* Header */}
                        <div className="mb-4">
                            <h1 className="text-[#0F172A] text-lg font-semibold m-0">
                                Analysis Plan
                            </h1>
                        </div>

                        {/* Execution Steps - Parent Transparent Card */}
                        <div className="execution-steps-container">
                            {/* Execution Steps Header - Figma design */}
                            <div className="execution-steps-header">
                                <img 
                                    src="assets/exec.svg" 
                                    alt="Execution Steps" 
                                    className="execution-steps-icon"
                                />
                                <div className="execution-steps-title-container">
                                    <h2 className="execution-steps-title">Execution Steps</h2>
                                    <p className="execution-steps-subtitle">Click and drag to reorder</p>
                                </div>
                            </div>

                            {/* Steps List */}
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={enabledSteps.map(step => step.step_id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {enabledSteps.map((step, index) => (
                                        <SortableStepCard
                                            key={step.step_id}
                                            step={step}
                                            index={index}
                                            disabled={isExecuting}
                                            isEditing={editingStepId === step.step_id}
                                            isDeleting={deletingStepId === step.step_id}
                                            isLocked={getBaseStepType(step.step_id) === "rca_analysis"}
                                            editableRef={(el) => { editableRefs.current[step.step_id] = el; }}
                                            onStartEdit={() => handleStartEdit(step.step_id)}
                                            onSaveEdit={() => handleSaveEdit(step.step_id)}
                                            onCancelEdit={() => handleCancelEdit(step.step_id)}
                                            onDelete={() => handleDeleteStep(step.step_id)}
                                            onConfirmDelete={() => handleConfirmDelete(step.step_id)}
                                            onCancelDelete={handleCancelDelete}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>

                            {/* Add Step Section - Inside the container */}
                            {!isExecuting && (
                                <div className="add-step-section">
                                    {!isAddingStep ? (
                                        <button
                                            className="add-step-btn"
                                            onClick={() => setIsAddingStep(true)}
                                            disabled={isExecuting}
                                        >
                                            <AddIcon sx={{ fontSize: 18 }} />
                                            Add Step
                                        </button>
                                    ) : !addStepType ? (
                                        <div className="add-step-menu">
                                            <p className="add-step-menu-label">Select step type:</p>
                                            <button
                                                className="add-step-option"
                                                onClick={() => {
                                                    setAddStepType("smart_column");
                                                    setNewStepReason(getDefaultReason("smart_column"));
                                                }}
                                            >
                                                <AutoFixHighIcon sx={{ fontSize: 18, color: "#068E75" }} />
                                                Generate Smart Columns
                                            </button>
                                            <button
                                                className="add-step-option"
                                                onClick={() => {
                                                    setAddStepType("pivot_table");
                                                    setNewStepReason(getDefaultReason("pivot_table"));
                                                }}
                                            >
                                                <TableChartIcon sx={{ fontSize: 18, color: "#068E75" }} />
                                                Create Pivot Table
                                            </button>
                                            <button
                                                className="add-step-cancel"
                                                onClick={() => setIsAddingStep(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="add-step-form">
                                            <p className="add-step-form-label">
                                                Step reason for {getStepTitle(addStepType)}:
                                            </p>
                                            <textarea
                                                className="add-step-reason-input"
                                                value={newStepReason}
                                                onChange={(e) => setNewStepReason(e.target.value)}
                                                placeholder="Describe why this step is needed..."
                                                rows={2}
                                            />
                                            <div className="add-step-form-actions">
                                                <button
                                                    className="add-step-confirm"
                                                    onClick={handleAddStep}
                                                    disabled={!newStepReason.trim()}
                                                >
                                                    Add
                                                </button>
                                                <button
                                                    className="add-step-cancel"
                                                    onClick={() => {
                                                        setAddStepType(null);
                                                        setNewStepReason("");
                                                        setIsAddingStep(false);
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Progress Sheet Overlay - Shows when executing */}
                        <ProgressSheet
                            isOpen={isExecuting}
                            progress={progress}
                            title="Executing analysis"
                            subtitle="Please wait while we run the analysis plan"
                            stepMessage={progressMessage}
                        />

                        {/* Global Validation Error */}
                        {validationError && (
                            <div ref={validationErrorRef} className="bg-red-100 rounded-md p-3 mb-4 flex items-start gap-2">
                                <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-white text-sm font-bold">!</span>
                                </div>
                                <p className="text-[13px] text-red-800 m-0 leading-snug">{validationError}</p>
                            </div>
                        )}

                    </div>
                </div>

                {/* Footer Section with Action Buttons */}
                <div className="w-full px-5 py-3" style={{ backgroundColor: 'rgba(211, 232, 217, 0.5)' }}>
                    <div className="w-full max-w-md mx-auto flex gap-3">
                        <button
                            onClick={onRegenerate}
                            disabled={isExecuting}
                            className={`flex-1 rounded-md p-3 px-4 text-[13px] font-semibold transition-all duration-200 h-11 flex items-center justify-center gap-2 ${
                                isExecuting
                                    ? 'bg-white/30 border border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed opacity-60'
                                    : 'bg-white border border-[#068E75] text-[#068E75] cursor-pointer hover:bg-[#068E75]/5'
                            }`}
                        >
                            <RefreshIcon sx={{ fontSize: 18 }} />
                            Regenerate
                        </button>
                        <button
                            onClick={() => onExecute(canSkipValidation)}
                            disabled={isExecuting}
                            className={`flex-1 border-none rounded-md p-3 px-4 text-[13px] font-semibold transition-all duration-200 shadow-md h-11 flex items-center justify-center gap-2 ${
                                isExecuting
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : canSkipValidation
                                        ? 'bg-[#D97706] text-white cursor-pointer hover:bg-[#B45309] hover:-translate-y-px hover:shadow-lg'
                                        : 'bg-[#068E75] text-white cursor-pointer hover:bg-[#057A63] hover:-translate-y-px hover:shadow-lg'
                            }`}
                            style={{
                                boxShadow: isExecuting ? 'none' : canSkipValidation ? '0 2px 4px rgba(217, 119, 6, 0.3)' : '0 2px 4px rgba(6, 142, 117, 0.3)'
                            }}
                        >
                            {isExecuting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                                        style={{ animation: 'spin 1s linear infinite' }}></div>
                                    Executing...
                                </>
                            ) : canSkipValidation ? (
                                <>
                                    <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                                    Proceed Further
                                </>
                            ) : (
                                <>
                                    <PlayArrowIcon sx={{ fontSize: 18 }} />
                                    Execute
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RCAPlanView;
